import { find } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ordersApi } from 'api';
import {
  AmountErrorDialog,
  AmountErrorDialogData,
  FormikSelectOption,
  PaginationWrapper,
} from 'components';
import { TIME_INTERVAL } from 'constants/time-interval.constants';
import { AdditionalOrderColumn, FilterDefinitionType, OrdersTab } from 'enums';
import {
  useWhiteTriangle,
  usePartialQuery,
  useQueryFilters,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { orderUtils } from 'utils';

import { OrderListWithAction } from '../OrderList';
import { OrdersActions } from '../OrdersActions';

type Props = {
  tab: OrdersTab;
  additionalSortingFields?: FormikSelectOption[];
  additionalColumns?: AdditionalOrderColumn[];
  additionalFilters?: FilterDefinition[];
};

export const Orders: React.FC<Props> = ({
  tab,
  additionalFilters = [],
  additionalSortingFields = [],
  additionalColumns = [],
}) => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { role, apiRole } = useUser();
  const { filters } = useQueryFilters<OrderFilters>();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const refetch = useMemo(() => tab === OrdersTab.Active, [tab]);

  const queryKey = useMemo(() => `${tab}-orders`, [tab]);
  const { isWhiteTriangle, requisitesId, renderLeftContent } =
    useWhiteTriangle();

  const queryResult = usePartialQuery(
    queryKey,
    ordersApi.getAllPaginatedAsRole(apiRole, tab),
    {
      useCachedCount: true,
      keepPreviousData: true,
      ...(refetch && {
        refetchInterval: TIME_INTERVAL.FIFTEEN_SECONDS,
        refetchIntervalInBackground: true,
      }),
    },
  );

  const [amountErrorDialogProps, setAmountErrorDialogProps] = useState<{
    open: boolean;
    data?: AmountErrorDialogData;
  }>({ open: false });

  const handleAmountErrorDialogOpen = useCallback(
    (data: AmountErrorDialogData) => {
      setAmountErrorDialogProps({ open: true, data });
    },
    [],
  );

  const handleAmountErrorDialogClose = useCallback(() => {
    setAmountErrorDialogProps({ open: false });
  }, []);

  const whiteTriangleFiltersDefinitions: FilterDefinition<OrderFilters>[] =
    useMemo(() => {
      if (!isWhiteTriangle) {
        return [];
      }
      return [
        {
          name: 'requisitesId',
          value: requisitesId,
          label: tFilters('requisites_id'),
          type: FilterDefinitionType.Text,
          hidden: true,
          removable: false,
        },
      ];
    }, [isWhiteTriangle, requisitesId, tFilters]);

  const selectedOrder = useMemo(
    () =>
      find(queryResult.data?.items, (order) => order.id === selectedOrderId) ||
      null,
    [queryResult.data, selectedOrderId],
  );

  return (
    <Fragment>
      <OrdersActions
        tab={tab}
        queryResult={queryResult}
        sortingFields={[
          ...orderUtils.getCommonPayinOrderSortingFields(),
          ...additionalSortingFields,
        ]}
        filtersDefinitions={[
          ...orderUtils.getCommonFilters(filters),
          ...additionalFilters,
          ...whiteTriangleFiltersDefinitions,
        ]}
      />
      <div className="tw-flex">
        {renderLeftContent && renderLeftContent()}
        {(!isWhiteTriangle || !!requisitesId) && (
          <div className="tw-flex-1 tw-min-w-0">
            <PaginationWrapper
              queryResult={queryResult}
              ignoreState={{ refetchLoading: refetch }}
            >
              <div className="tw-flex">
                <OrderListWithAction
                  selectedOrder={selectedOrder}
                  items={queryResult.data?.items || []}
                  queryKey={queryKey}
                  handleOrderSelection={setSelectedOrderId}
                  columns={orderUtils.getPayinOrderColumns(additionalColumns, {
                    queryKey,
                    role,
                    isWhiteTriangle,
                    handleAmountErrorDialogOpen,
                  })}
                  handleAmountErrorDialogOpen={handleAmountErrorDialogOpen}
                />
              </div>
            </PaginationWrapper>
          </div>
        )}
      </div>
      <AmountErrorDialog
        {...amountErrorDialogProps}
        onClose={handleAmountErrorDialogClose}
      />
    </Fragment>
  );
};
